import { getAnalytics, logEvent } from "firebase/analytics";
import { AnimatePresence, motion } from "framer-motion";
import { ReactNode, useEffect } from "react";
import { useLoaderData, useLocation } from "react-router-dom";
import styled from "styled-components";
import { isProduction } from "../Utils/environment";
import { scrollToAnchor } from "../Utils/scroll";
import Navbar from "./Navbar";

interface PageShellProps {
  children: ReactNode;
  overrideTitle?: string;
}

export const PageShell = ({
  children,
  overrideTitle,
}: PageShellProps): JSX.Element => {
  const pageRouteData = useLoaderData();
  const { pathname } = useLocation();

  useEffect(() => {
    document.addEventListener("gesturestart", function (e) {
      e.preventDefault();
    });

    document.addEventListener("gesturechange", function (e) {
      e.preventDefault();
    });

    document.addEventListener("gestureend", function (e) {
      e.preventDefault();
    });
  }, []);

  useEffect(() => {
    // Check if URL contains hash fragment
    const hash = `/${window.location.hash}`;

    if (hash) {
      // Remove the /#/ prefix to get the anchor name
      const anchorName = hash.replace("/#/", "");
      const element = document.getElementById(anchorName);
      if (element && anchorName) {
        setTimeout(() => {
          scrollToAnchor({ title: "", link: hash });

          // Clear the URL hash without triggering a page reload
          window.history.replaceState(null, "", window.location.pathname);
        }, 1);
      }
    }
  }, [pathname]);

  useEffect(() => {
    if (pageRouteData != null) {
      if (overrideTitle != null) {
        document.title = overrideTitle;
      } else {
        if ((pageRouteData as any).pageTitle != null) {
          const pageTitle = (pageRouteData as any).pageTitle;
          if ((pageTitle as string).length > 0) {
            document.title = `${
              pageTitle !== "Home"
                ? `${pageTitle} // Pencil to Polish`
                : "Pencil to Polish // Design + Development + Innovation."
            }`;
          }
        }
      }
    }
  }, [pageRouteData, overrideTitle]);

  useEffect(() => {
    // Scroll to top of page on router change
    window.scrollTo(0, 0);

    const analytics = isProduction() ? getAnalytics() : null;

    if (analytics) {
      logEvent(analytics, "page_view", {
        page_location: pathname,
        page_title: document.title,
      });
    }
  }, [pathname]);

  return (
    <PageShellWrapper>
      <AnimatePresence>
        <Navbar key={"navbar"} />
        <motion.div
          initial={{ opacity: 0, top: pathname === "/" ? "0" : "1rem" }}
          exit={{ opacity: 0, top: pathname === "/" ? "0" : "1rem" }}
          animate={{ opacity: 1, top: "0rem" }}
          style={{ position: "relative" }}
          key={"page-content"}
          id={"main-content"}
        >
          {children}
        </motion.div>
      </AnimatePresence>
    </PageShellWrapper>
  );
};

const PageShellWrapper = styled.div`
  background: linear-gradient(
    180deg,
    #013b49 0%,
    #032027 40%,
    #022e38 63%,
    #032730 80%
  );
`;
